import { IDisplayColumn } from 'src/manageAction/interfaces/ManageAction';
import { IFieldDefinition } from './FieldDefinition';

//Sync with tenantProvider value here
export enum TenantProvider {
  ARTEMIS = 'ARTEMIS',
  ADO = 'ADO',
}

export enum TenantNames {
  RedButton = 'Red Button',
  CAPSL = 'CAPSL',
  Cosell = 'CoSell',
  AOAICapacity = 'Azure AI Capacity',
  TeamsAPEX = 'TeamsAPEX',
  MWCompete = 'MW Compete',
  CSG = 'CSG',
  ECIF = 'ECIF',
  GTT = 'GTT',
  MCAPSHelpPM = 'MCAPS Help PM',
  QBCVSU = 'QBC-VSU',
  UATGeneral = 'UAT General',
  GPSInvestments = 'Partner Investments',
  MCAPSRoB = 'MCAPS Leader RoB',
  MACCSupport = 'MACC Unified Purple Empowerment',
  DefenderForCloud = 'MDC Attach Purple Exception',
  PartnerCoMarketing = 'Partner Marketing Yield Tracker',
  SGS = 'SGS',
  MCAPSMaaS = 'MaaS',
  GSI = 'GSI ACO PIR',
  TechROB = 'Tech RoB',
  Compete = 'Compete',
  MicrosoftFederal = 'Microsoft Federal',
  AzureSponsorship = 'Azure Sponsorship for ISV',
  AMM = 'AMM Offer Desk',
  Approvals = 'Approvals',
  ComplianceCheckList = 'Compliance Checklist',
  CalculationInformation = 'Calculation Information',
  NonAICapacity = 'Non AI Capacity',
  PartnerSkilling = 'Regional Skilling',
  LaunchChecklist = 'Launch Information',
  MEA = 'MEA Bedrock',
  MicrosoftSecurityResearch = 'Microsoft Security Research',
  Marketplace = 'Microsoft Marketplace',
}

export enum TenantInternalNames {
  UATGeneral = 'UATGeneral',
}

export type ITenantConfig = {
  [key: string]: ITenant & ITenantUIConfig;
};

export interface ITenantUIConfig {
  nodeName: string[]; // Need to understand the usecase of this field and based on that setup additional hierarchy. At this moment all config is at tenant level.
  provider: TenantProvider;
  TenantExperience: boolean;
  FeatureConfig: IFeatureConfig;
  isMdaEnabledForECIF?: boolean | null;
  isMdaEnabledForACO?: boolean | null;
  isMdaEnabledForAA?: boolean | null;
  isMdaEnabledForBenchMarkTCO?: boolean | null;
  isMdaEnabledForACOInECIF?: boolean | null;
  isNbarEnabled?: boolean | null;
  /**
   * Hardcoded escape hatch to temporarily disable flight-based visibility gating for this tenant
   * (see App.tsx tenant-injection logic). When `true`, the tenant is treated as visible to all users
   * regardless of `requiredFlights`/flight membership. Defaults to `false`/`undefined` (flighting
   * enforced). Intended to be short-lived — remove once real flighting is ready to enforce.
   */
  bypassFlightCheck?: boolean;
  /**
   * Hardcoded escape hatch to temporarily disable the server-driven MANAGE (edit) form for this tenant
   * and fall back to the legacy manage-action form component instead. When `true`, the tab renders the
   * legacy form; when `false`/`undefined` (default), the server-driven form renders. Intended to be
   * short-lived — remove once the server-driven manage form (GET forms/{id} + PATCH) is ready.
   */
  disableServerDrivenManageForm?: boolean;
}

export interface IFeatureConfig {
  /**
   * Controls whether sensitive info validation (JWT, AWS keys, SAS URLs, etc.) is applied
   * to text fields and rich text editors on this tenant's tabs.
   *
   * - When `true`: Sensitive info validation is enabled.
   * - When `false`: Sensitive info validation is disabled.
   * - When `undefined` (default): Follows the tenant's provider type —
   *   ADO tenants are enabled, Artemis tenants are disabled.
   *
   * To enable for an Artemis tenant, set this to `true` in that tenant's config file.
   */
  SensitiveInfoValidation?: boolean;
  /**
   * Controls whether sensitive info validation errors are counted and displayed
   * in the error badge on tabs and block save.
   *
   * - When `true`: Errors contribute to the badge count and block save.
   * - When `false`: Only field-level inline warnings are shown; no badge count or save blocking.
   * - When `undefined` (default): Treated as `true`.
   *
   * Only meaningful when `SensitiveInfoValidation` is enabled.
   */
  SensitiveInfoErrorCount?: boolean;
  SensativeInfoErrorCount?: boolean;
  FieldConfig?: IFilterFieldConfig[];
  AttachmentConfigurations: IAttachmentConfigurations[];
  Navigation: INavigationConfigurations;
  Hub: {
    Title: string;
    ADOTitle: string;
    Create: ICreateActionMenuItem[];
    hideCreateButton: boolean;
    Download: boolean;
    Email: boolean;
    Filter: boolean;
    FilterConfig: IFilterFieldConfig[];
    AreaPath: string[];
    LocalSearch: boolean;
    ColumnOptions: boolean;
    ColumnOptionsConfig: IColumnOptionsConfig;
    DownloadTemplateConfig: IDownloadTemplateConfig;
    SplitMode: boolean;
    BulkEdit: boolean;
    BulkDelete?: boolean;
    BulkEditConfig: IBulkEditConfig;
    BulkUpload: boolean;
    DownloadTemplate: boolean;
    CreateRAID: boolean;
    RelatedItem: boolean;
  };
  ManageAction: {
    NewExperience: boolean;
    MandateFormPickers: Array<{ formFieldName: string; formFieldLabel: string; apiFieldName: string; allowedList?: string[] }>;
    AllowedOfferKeysForValidation?: Array<string>;
    AllowedOfferKeysForCaseTracking?: Array<string>;
    // isOldRedirectionAvailable: boolean;
    Header: {
      Tags: boolean;
      Actions: {
        Follow: boolean;
        Email: boolean;
        CopyLink: boolean;
      };
    };
    Tabs: {
      TechnicalFeedback: boolean;
      RelatedItems: {
        showRelatedTab: boolean;
        showTFTCreated: boolean;
        showRemoteRelatedLink?: boolean;
      };
      Attachments: boolean;
      History: boolean;
      NBAR?: boolean;
    };
    Panes: {
      CopilotInsights: {
        CustomerInsights: boolean;
        DiscussionInsights: boolean;
      };
      StatusUpdate: boolean;
      ClosureStatement: boolean;
      Discussion: boolean;
      Tasks: boolean;
      EmailConversation: boolean;
    };
  };
  Tabs?: {
    TechnicalFeedback: boolean;
    RelatedItems: {
      showRelatedTab: boolean;
      showTFTCreated: boolean;
      showRemoteRelatedLink?: boolean;
    };
    Attachments: boolean;
    History: boolean;
  };
  Panes?: {
    CopilotInsights: {
      CustomerInsights: boolean;
      DiscussionInsights: boolean;
    };
    StatusUpdate: boolean;
    ClosureStatement: boolean;
    Discussion: boolean;
    Tasks: boolean;
    EmailConversation: boolean;
  };
  ManageView: IManageViewConfig;
}

export interface IAttachmentConfigurations {
  AttachmentId: string | null;
  AttachmentType: string;
  Required: boolean;
  FileFormats: string[];
  DisplayFileFormats: string[];
  SizeLimit: number;
  FileLimit: number;
  MaxSizeLimit: number;
  Description: string;
}

export interface INavigationConfigurations {
  GlobalSearch: boolean;
  Copilot: boolean;
  Analytics: boolean;
  RequestAccess: boolean;
  Settings: boolean;
  Notifications: boolean;
  Help: boolean;
};

export interface ICreateActionMenuItem {
  Text: string;
  Key: string;
  NodeName: string;
}

export interface IFilterFieldConfig {
  fieldId: string;
  fieldName: string;
  fieldType: number;
  internalName?: string;
  IFilterObject?: string; // TODO: Define this type after discussing with Saravana
  responseFormat?: string;
  isDefaultField?: boolean;
  fieldDropdownName?: string;
  keyName?: string;
}

export interface IManageViewConfig {
  isFieldConfigurationEnabled?: boolean;
  isArtemisIntegrationEnabled: boolean;
  showAddViewButton: boolean;
  showAddFolderButton: boolean;
  showCommandBar: boolean;
  showPreviewFeatureMessageBar: boolean;
  showSelectFields: boolean;
  defaultViewId?: string;
  systemViewsFolderId?: string;
  sharedViewsFolderId?: string;
  defaultColumns?: IDisplayColumn[];
  filterableColumns?: string[];
  selectableColumns?: string[];
  dropdownValues: Record<string, string[]>;
}

export interface IColumnOptionsConfig {
  // TODO: Define this type after discussing with Saravana
}

export interface IBulkEditConfig {
  // TODO: Define this type
}

// Tenant API response types
export interface TenantsResponse {
  '@odata.context': string;
  value: ITenant[];
}

export interface ITenant {
  TenantID: string;
  ApplicationType: string;
  PlatformId: string;
  Tenant: string;
  Enabled: boolean;
  AssignedTeams: string[];
  ShortName: string;
  Default: boolean;
  ArtemisCall: boolean;
  ArtemisEnabled: string[];
  id: string;
  etag: string | null;
  AreaPathMap: AreaPathMap;
  FieldConfigurations?: IFieldDefinition[] | null;
}

export interface AreaPathMap {
  Paths: string[];
}
export interface IDownloadTemplateConfig {
  // TODO: Define this type later on
}
export interface SGSRegionsTransformResult {
    transformedColumns: Array<{ InternalName: string; DisplayName: string; IsDefault: boolean; CustomWidth: number }>;
    transformedData: any[];
}
// Generated by GitHub Copilot | Updated on: 2026-07-01 | 2 lines modified | Added MicrosoftSecurityResearch to TenantNames enum
// Copilot change tracking: 2026-11-02 frontend-regressions: Declare optional MDA and NBAR feature flags consumed by typed tenant helpers.
